import React, { useState } from "react";
import { api } from "@/lib/api";
import { unwrapPage, usePagedList } from "@/lib/paging";
import { ListPager } from "@/components/ui/list-pager";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Handshake, Plus, Edit, Trash2, Loader2, RefreshCw, Search } from "lucide-react";
import type { AgentRecord } from "@/types";

const EMPTY_FORM = { name: "", phone: "", email: "", city: "", state: "", commission_pct: "", notes: "", status: true };

export function AgentsPage() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"ACTIVE" | "INACTIVE" | "ALL">("ACTIVE");
  const [actionLoading, setActionLoading] = useState(false);
  // null = closed, "new" = add, record = edit
  const [dialogItem, setDialogItem] = useState<AgentRecord | "new" | null>(null);
  const [deleteItem, setDeleteItem] = useState<AgentRecord | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const { success, error } = useToast();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission("agent.create");
  const canUpdate = hasPermission("agent.update");
  const canDelete = hasPermission("agent.delete");

  const list = usePagedList<AgentRecord>({
    deps: [search, statusFilter],
    debounceMs: search.trim() ? 350 : 0,
    load: async (page) => {
      const qs = new URLSearchParams({ page: String(page), page_size: "30" });
      if (search.trim()) qs.set("search", search.trim());
      try {
        const res = unwrapPage<AgentRecord>(await api.get(`/api/Agent/List?${qs}`));
        // The list API only filters active/all, so the inactive view is narrowed here
        const rows = statusFilter === "ACTIVE" ? res.rows.filter((a) => a.status !== false) : statusFilter === "INACTIVE" ? res.rows.filter((a) => a.status === false) : res.rows;
        return { ...res, rows };
      } catch (err: any) {
        error("Fetch Error", err.message || "Failed to load agents");
        return { rows: [], total: 0, page, page_size: 30 };
      }
    },
  });
  const agents = list.rows;

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setDialogItem("new");
  };

  const openEdit = (a: AgentRecord) => {
    setForm({
      name: a.name,
      phone: a.phone || "",
      email: a.email || "",
      city: a.city || "",
      state: a.state || "",
      commission_pct: String(Number(a.commission_pct)),
      notes: a.notes || "",
      status: a.status !== false,
    });
    setDialogItem(a);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      error("Validation Error", "Agent name is required");
      return;
    }
    const commission = form.commission_pct === "" ? 0 : Number(form.commission_pct);
    if (!(commission >= 0 && commission <= 100)) {
      error("Validation Error", "Commission must be between 0 and 100");
      return;
    }
    const payload = {
      name: form.name.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      city: form.city.trim(),
      state: form.state.trim(),
      commission_pct: commission,
      notes: form.notes.trim(),
      status: form.status,
    };
    setActionLoading(true);
    try {
      if (dialogItem === "new") {
        await api.post("/api/Agent/Insert", payload);
        success("Agent Added", "Agent added successfully");
      } else if (dialogItem) {
        await api.put(`/api/Agent/Update/${dialogItem.id}`, payload);
        success("Agent Updated", "Agent saved successfully");
      }
      setDialogItem(null);
      await list.reload();
    } catch (err: any) {
      error("Save Failed", err.message || "Failed to save agent");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteItem) return;
    setActionLoading(true);
    try {
      const res: any = await api.delete(`/api/Agent/Delete/${deleteItem.id}`);
      success("Agent Removed", res?.Message || "Agent deleted successfully");
      setDeleteItem(null);
      await list.reload();
    } catch (err: any) {
      error("Delete Failed", err.message || "Failed to delete agent");
    } finally {
      setActionLoading(false);
    }
  };

  const selectClass =
    "h-9 sm:h-10 rounded-md border border-border bg-background px-3 text-xs sm:text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary";

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Agents &amp; Brokers</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Agents who bring buyers, with the commission they usually earn</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={list.reload} disabled={list.loading} className="h-9 gap-1.5">
            <RefreshCw className={`size-3.5 ${list.loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          {canCreate && (
            <Button size="sm" onClick={openAdd} className="h-9 gap-1.5 shadow-sm">
              <Plus className="size-4" />
              Add Agent
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none z-10" />
          <Input placeholder="Search by name, phone, city or email..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 h-9 text-sm" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)} className={`${selectClass} sm:w-36`}>
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
          <option value="ALL">All</option>
        </select>
      </div>

      {list.loading && agents.length === 0 ? (
        <TableSkeleton rows={6} columns={6} />
      ) : (
        <div className="rounded-lg border border-border bg-card shadow-xs overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Agent</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>City</TableHead>
                <TableHead className="text-right">Commission</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {agents.map((a) => (
                <TableRow key={a.id}>
                  <TableCell>
                    <div className="flex items-center gap-2.5">
                      <div className="size-8 rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400 flex items-center justify-center shrink-0">
                        <Handshake className="size-4" />
                      </div>
                      <div className="font-semibold text-sm text-foreground">{a.name}</div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    <div>{a.phone || "-"}</div>
                    {a.email && <div className="text-xs">{a.email}</div>}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{[a.city, a.state].filter(Boolean).join(", ") || "-"}</TableCell>
                  <TableCell className="text-right text-sm font-semibold">{Number(a.commission_pct)}%</TableCell>
                  <TableCell>
                    <Badge variant={a.status !== false ? "success" : "secondary"}>{a.status !== false ? "Active" : "Inactive"}</Badge>
                  </TableCell>
                  <TableCell className="text-right space-x-1 whitespace-nowrap">
                    {canUpdate && (
                      <Button variant="ghost" size="icon-sm" onClick={() => openEdit(a)} title="Edit Agent">
                        <Edit className="size-4 text-muted-foreground hover:text-foreground" />
                      </Button>
                    )}
                    {canDelete && (
                      <Button variant="ghost" size="icon-sm" onClick={() => setDeleteItem(a)} title="Delete Agent">
                        <Trash2 className="size-4 text-red-500 hover:text-red-700" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {agents.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-muted-foreground text-sm">
                    No agents found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <ListPager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} disabled={list.loading} />
        </div>
      )}

      <Dialog open={!!dialogItem} onOpenChange={(open) => !open && setDialogItem(null)}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-bold">{dialogItem === "new" ? "Add Agent" : "Edit Agent"}</DialogTitle>
            <DialogDescription>The commission is copied onto a deal when this agent is set on it, and can be changed per deal.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5 py-2">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              <div className="space-y-2">
                <Label>City</Label>
                <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>State</Label>
                <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Commission %</Label>
                <Input type="number" min={0} max={100} step="0.01" value={form.commission_pct} onChange={(e) => setForm({ ...form, commission_pct: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <div className="flex items-center justify-between rounded-xl border border-border px-4 py-3 bg-muted/20">
              <div>
                <Label className="cursor-pointer">Active</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Inactive agents can't be picked on new records</p>
              </div>
              <Switch checked={form.status} onCheckedChange={(checked) => setForm({ ...form, status: checked })} />
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogItem(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionLoading}>
                {actionLoading ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
                Save Agent
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteItem} onOpenChange={(open) => !open && setDeleteItem(null)}>
        <DialogContent className="sm:max-w-lg md:max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-bold text-red-600">Delete Agent</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Remove <strong className="text-foreground">{deleteItem?.name}</strong>? If the agent is linked to any buyer, lead or deal, it is deactivated instead so history stays.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-3">
            <Button type="button" variant="outline" onClick={() => setDeleteItem(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
